<template>
  <v-dialog :value="value" max-width="720" scrollable @input="$emit('input', $event)">
    <v-card v-if="log" class="kf-log-dialog">
      <v-card-title class="kf-log-dialog__head">
        <div class="kf-log-dialog__id">
          <div class="kf-log-dialog__title">{{ log.title }}</div>
          <div class="kf-log-dialog__sub">
            {{ log.log_id }} · {{ sourceLabel }} ·
            {{ fmt.dateTimeIst(log.created) }}
          </div>
        </div>
        <v-spacer />
        <v-btn icon small @click="$emit('input', false)">
          <LucideIcon name="x" :size="20" />
        </v-btn>
      </v-card-title>

      <v-card-text class="kf-log-dialog__body">
        <dl class="kf-log-dialog__grid">
          <dt>Customer</dt>
          <dd>
            {{ log.customer_name || "-" }} · User {{ log.user_id }} ·
            {{ log.mobile || "-" }}
          </dd>

          <dt>Sent by</dt>
          <dd>{{ log.sender_label || "-" }}</dd>

          <dt>Channel</dt>
          <dd>{{ log.channel || "-" }}<template v-if="log.contact"> → {{ log.contact }}</template></dd>

          <dt>Template</dt>
          <dd>{{ log.template_name || "-" }}</dd>

          <dt>Step</dt>
          <dd><code v-if="log.step_key" class="kf-code">{{ log.step_key }}</code><span v-else>-</span></dd>

          <dt>Reference</dt>
          <dd><code v-if="log.reference" class="kf-code">{{ log.reference }}</code><span v-else>-</span></dd>

          <dt>Outcome</dt>
          <dd>
            {{ log.outcome_label }}
            <span v-if="log.outcome_group === 'skipped' || log.outcome_detail" class="kf-muted">
              ({{ log.outcome }}<template v-if="log.outcome_detail">: {{ log.outcome_detail }}</template>)
            </span>
          </dd>

          <dt>Status</dt>
          <dd>
            <template v-if="log.borrower_type_label">
              {{ log.borrower_type_label }}
              <span class="kf-muted">(when the message was triggered)</span>
            </template>
            <span v-else-if="log.source === 'received'" class="kf-muted">
              Not recorded for messages sent outside the notification engine.
            </span>
            <span v-else class="kf-muted">
              Not recorded: logged before the borrower's status was recorded.
            </span>
          </dd>
        </dl>

        <div class="kf-log-dialog__label">Subject / title</div>
        <div class="kf-log-dialog__text">{{ log.subject || "(none)" }}</div>

        <div class="kf-log-dialog__label">Message body (as stored)</div>
        <pre class="kf-log-dialog__text kf-log-dialog__pre">{{ log.body || "(no body stored)" }}</pre>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import fmt from "@/app-js/karrofin/format";

/**
 * One Notification History row in full: the exact stored body, template and
 * step. Reads the row it is given -- no request, nothing re-rendered,
 * so what it shows is what was logged.
 */
export default {
  name: "KfNotificationLogDialog",
  props: {
    value: { type: Boolean, default: false },
    log: { type: Object, default: null },
  },
  data() {
    return { fmt };
  },
  computed: {
    sourceLabel() {
      if (this.log.source_label) return this.log.source_label;
      return this.log.source === "dry_run" ? "Dry run" : "Real send";
    },
  },
};
</script>

<style scoped>
.kf-log-dialog__head {
  align-items: flex-start;
  flex-wrap: nowrap;
}
.kf-log-dialog__id {
  min-width: 0;
}
.kf-log-dialog__title {
  font-size: var(--kf-fs-body);
  font-weight: 700;
  color: #000;
  overflow-wrap: normal;
}
.kf-log-dialog__sub {
  font-size: var(--kf-fs-label);
  color: #54636f;
  font-weight: 400;
}
.kf-log-dialog__grid {
  display: grid;
  grid-template-columns: 140px 1fr;
  gap: 6px 12px;
  margin: 0 0 14px;
  font-size: var(--kf-fs-hint);
  color: #000;
}
.kf-log-dialog__grid dt {
  color: #54636f;
  font-weight: 600;
}
.kf-log-dialog__grid dd {
  margin: 0;
  min-width: 0;
}
.kf-log-dialog__label {
  font-size: var(--kf-fs-label);
  color: #54636f;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  margin: 10px 0 4px;
}
.kf-log-dialog__text {
  font-size: var(--kf-fs-hint);
  color: #000;
}
.kf-log-dialog__pre {
  white-space: pre-wrap;
  font-family: inherit;
  background: var(--kf-surface-2);
  border-radius: 8px;
  padding: 10px 12px;
  margin: 0;
}
.kf-code {
  font-size: var(--kf-fs-label);
  background: var(--kf-hover);
  color: var(--kf-brand);
  border-radius: 4px;
  padding: 2px 6px;
}
.kf-muted {
  color: #54636f;
  font-size: var(--kf-fs-label);
}
@media (max-width: 600px) {
  .kf-log-dialog__grid {
    grid-template-columns: 1fr;
  }
}
</style>
